// src/components/explorers/wildfireExplorer/tabs/FilesTab.tsx
// Fire file and weather spot forecast listing + single-file upload via JSON:API

import React, { useState, useEffect, useMemo } from 'react';
import { WildfireEntity } from '../../../../types/wildfire';
import { getCmsUrl, getCmsApiUrl } from '../../../../services/configService';
import { proxyCmsUrl } from '../../../../services/cmsFileProxy';
import { useWildfireSearch } from '../../../../hooks/useWildfireSearch';
import MultiFileUploader from '../../../upload/MultiFileUploader';
import { usePermissions } from '../../../../context/PermissionContext';
import './FilesTab.scss';

interface FilesTabProps {
  wildfire: WildfireEntity;
}

// ============================================================================
// Shared card data model and component
// ============================================================================
interface FileCardData {
  id: string;
  heading: string;       // subcategory preferred, falls back to title
  title: string;         // raw title for filename line
  fileDate: string | null;
  fireNumber: string | null;
  region: string | null;
  typeLabel: string;     // "Fire File" | "Spot Forecast"
  typeClass: string;     // css modifier
  description: string | null; // body field, truncated
  fileUrl: string | null;
  thumbnailUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  nodeUrl: string;       // link to the node in CMS
}

const FileCard: React.FC<{ card: FileCardData }> = ({ card }) => {
  const isImage = card.mimeType?.startsWith('image/');
  const thumbSrc = card.thumbnailUrl ?? null;

  return (
    <a
      className="file-card"
      href={card.nodeUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className={`file-card__thumb file-card__thumb--${card.typeClass}`}>
        {isImage && thumbSrc ? (
          <img src={thumbSrc} alt="" loading="lazy" />
        ) : (
          <span className="file-card__icon" aria-hidden="true">
            {card.typeClass === 'fire-file' ? '🔥' : '🌤'}
          </span>
        )}
      </div>

      <div className="file-card__body">
        <h4 className="file-card__heading">{card.heading}</h4>
        {card.fileDate && <div className="file-card__date">{card.fileDate}</div>}


        {card.description && (
          <div className="file-card__description" title={card.description}>
            {card.description.length > 200
              ? card.description.slice(0, 200) + '...'
              : card.description}
          </div>
        )}
      </div>
    </a>
  );
};

// ============================================================================
// JSON:API fetch helpers
// ============================================================================
const CARD_LIMIT = 5;

// Resolve a taxonomy term name from JSON:API included array
// Strip HTML tags from body field value
const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, '').trim();

const resolveTermName = (
  included: any[],
  relationship: any | undefined,
): string | null => {
  const ref = relationship?.data;
  if (!ref) return null;
  const term = included.find((i: any) => i.id === ref.id && i.type === ref.type);
  return term?.attributes?.name ?? null;
};

const resolveFile = (
  included: any[],
  relationship: any | undefined,
): { url: string | null; thumbnailUrl: string | null; name: string | null; mime: string | null } => {
  const ref = relationship?.data;
  if (!ref) return { url: null, thumbnailUrl: null, name: null, mime: null };
  const file = included.find((i: any) => i.id === ref.id && i.type === ref.type);
  return {
    url: proxyCmsUrl(file?.attributes?.uri?.url ?? null),
    thumbnailUrl: proxyCmsUrl(file?.attributes?.image_style_uri?.thumbnail ?? null),
    name: file?.attributes?.filename ?? null,
    mime: file?.attributes?.filemime ?? null,
  };
};

// ============================================================================
// Hook: fetch a list of fire_file or weather_file nodes for a given fire
// ============================================================================
interface UseFileListResult {
  cards: FileCardData[];
  total: number;       // exact count when <= CARD_LIMIT, otherwise CARD_LIMIT
  hasMore: boolean;    // true when there are more than CARD_LIMIT results
  loading: boolean;
  error: string | null;
}

const useFireFiles = (fireNumber: string, refreshKey: number): UseFileListResult => {
  const [result, setResult] = useState<UseFileListResult>({
    cards: [], total: 0, hasMore: false, loading: true, error: null,
  });
  const API_URL = getCmsApiUrl();
  const CMS_URL = getCmsUrl();

  useEffect(() => {
    if (!CMS_URL || !fireNumber) {
      setResult({ cards: [], total: 0, hasMore: false, loading: false, error: null });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setResult(r => ({ ...r, loading: true, error: null }));
        const params = new URLSearchParams({
          'filter[field_wildfire_ref.name]': fireNumber,
          'include': 'field_fire_subcategory,field_file,field_region',
          'sort': '-field_file_date',
          'page[limit]': String(CARD_LIMIT),
        });
        const res = await fetch(
          `${API_URL}/jsonapi/node/fire_file?${params}`,
          { headers: { 'Accept': 'application/vnd.api+json' }, credentials: 'include' as RequestCredentials },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const included = data.included || [];
        const nodes = data.data || [];
        const totalCount = data.meta?.count ?? nodes.length;

        const cards: FileCardData[] = nodes.map((node: any) => {
          const subcategory = resolveTermName(included, node.relationships?.field_fire_subcategory);
          const region = resolveTermName(included, node.relationships?.field_region);
          const file = resolveFile(included, node.relationships?.field_file);
          const bodyRaw = node.attributes.body?.value || node.attributes.body?.processed || '';
          return {
            id: node.id,
            heading: subcategory || node.attributes.title,
            title: node.attributes.title,
            fileDate: node.attributes.field_file_date,
            fireNumber,
            region,
            typeLabel: 'Fire File',
            typeClass: 'fire-file',
            description: bodyRaw ? stripHtml(bodyRaw) : null,
            fileUrl: file.url,
            thumbnailUrl: file.thumbnailUrl,
            fileName: file.name,
            mimeType: file.mime,
            nodeUrl: `${CMS_URL}/node/${node.attributes.drupal_internal__nid}`,
          };
        });

        if (!cancelled) {
          setResult({
            cards,
            total: totalCount,
            hasMore: totalCount > CARD_LIMIT,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setResult({
            cards: [],
            total: 0,
            hasMore: false,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load files',
          });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [API_URL, CMS_URL, fireNumber, refreshKey]);

  return result;
};

const useSpotForecasts = (fireNumber: string, refreshKey: number): UseFileListResult => {
  const [result, setResult] = useState<UseFileListResult>({
    cards: [], total: 0, hasMore: false, loading: true, error: null,
  });
  const API_URL = getCmsApiUrl();
  const CMS_URL = getCmsUrl();

  useEffect(() => {
    if (!CMS_URL || !fireNumber) {
      setResult({ cards: [], total: 0, hasMore: false, loading: false, error: null });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setResult(r => ({ ...r, loading: true, error: null }));
        // Weather files link to fires via field_wildfire_ref (taxonomy reference)
        const params = new URLSearchParams({
          'filter[field_wildfire_ref.name]': fireNumber,
          'include': 'field_weather_subcategory,field_file,field_region',
          'sort': '-field_file_date',
          'page[limit]': String(CARD_LIMIT),
        });
        const res = await fetch(
          `${API_URL}/jsonapi/node/weather_file?${params}`,
          { headers: { 'Accept': 'application/vnd.api+json' }, credentials: 'include' as RequestCredentials },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const included = data.included || [];
        const nodes = data.data || [];
        const totalCount = data.meta?.count ?? nodes.length;

        const cards: FileCardData[] = nodes.map((node: any) => {
          const subcategory = resolveTermName(included, node.relationships?.field_weather_subcategory);
          const region = resolveTermName(included, node.relationships?.field_region);
          const file = resolveFile(included, node.relationships?.field_file);
          const bodyRaw = node.attributes.body?.value || node.attributes.body?.processed || '';
          return {
            id: node.id,
            heading: subcategory || node.attributes.title,
            title: node.attributes.title,
            fileDate: node.attributes.field_file_date,
            fireNumber,
            region,
            typeLabel: 'Spot Forecast',
            typeClass: 'weather-file',
            description: bodyRaw ? stripHtml(bodyRaw) : null,
            fileUrl: file.url,
            thumbnailUrl: file.thumbnailUrl,
            fileName: file.name,
            mimeType: file.mime,
            nodeUrl: `${CMS_URL}/node/${node.attributes.drupal_internal__nid}`,
          };
        });

        if (!cancelled) {
          setResult({
            cards,
            total: totalCount,
            hasMore: totalCount > CARD_LIMIT,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setResult({
            cards: [],
            total: 0,
            hasMore: false,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load spot forecasts',
          });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [API_URL, CMS_URL, fireNumber, refreshKey]);

  return result;
};

// ============================================================================
// File section (shared by fire files + spot forecasts)
// ============================================================================
interface FileSectionProps {
  heading: string;
  cards: FileCardData[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  viewAllUrl: string | null;
  emptyLabel: string;
  actions?: React.ReactNode;
}

const FileSection: React.FC<FileSectionProps> = ({
  heading, cards, total, hasMore, loading, error, viewAllUrl, emptyLabel, actions,
}) => {
  const { canDo } = usePermissions();
  const canViewCms = canDo('cms', 'read');
  return (
  <section className="detail-section file-section">
    <div className="section-header">
      <div className="section-header__left">
        {!loading && cards.length > 0 && (
          <span className="section-header__summary">
            Showing latest {cards.length}
          </span>
        )}
      </div>
      <div className="section-header__right">
        {viewAllUrl && canViewCms && !loading && cards.length > 0 && (
          <a
            className="btn btn-view-all"
            href={viewAllUrl}
          >
            View all
          </a>
        )}
        {actions}
      </div>
    </div>

    {loading && <p className="files-muted">Loading...</p>}
    {error && <p className="files-warning">Error: {error}</p>}
    {!loading && !error && cards.length === 0 && (
      <p className="files-muted">{emptyLabel}</p>
    )}

    {cards.length > 0 && (
      <div className="file-card-list">
        {cards.map(c => <FileCard key={c.id} card={c} />)}
      </div>
    )}
  </section>
  );
};

// Old UploadButton and UploadForm removed — replaced by MultiFileUploader

// ============================================================================
// Main FilesTab
// ============================================================================
type SubTab = 'fire-files' | 'spot-forecasts';

export const FilesTab: React.FC<FilesTabProps> = ({ wildfire }) => {
  const fireNumber = wildfire.fire_number;
  const [activeTab, setActiveTab] = useState<SubTab>('fire-files');
  const [isUploaderOpen, setIsUploaderOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const CMS_URL = getCmsUrl();

  const fireFiles = useFireFiles(fireNumber, refreshKey);
  const spotForecasts = useSpotForecasts(fireNumber, refreshKey);

  // Resolve wildfire UUID from CMS taxonomy (needed by the upload pipeline)
  const [wildfireUuid, setWildfireUuid] = useState('');
  const { results: wildfireResults, search: searchWildfires } = useWildfireSearch();

  useEffect(() => {
    if (fireNumber) searchWildfires(fireNumber);
  }, [fireNumber, searchWildfires]);

  useEffect(() => {
    if (wildfireResults.length > 0 && !wildfireUuid) {
      const match = wildfireResults.find(
        w => w.name.toLowerCase() === fireNumber.toLowerCase(),
      );
      if (match) setWildfireUuid(match.id);
    }
  }, [wildfireResults, fireNumber, wildfireUuid]);

  const viewAllUrl = useMemo(() => {
    if (!CMS_URL || !fireNumber) return null;
    return `${CMS_URL}/files?wildfire=${encodeURIComponent(fireNumber)}`;
  }, [CMS_URL, fireNumber]);

  const handleUploadComplete = () => {
    setIsUploaderOpen(false);
    setStatusMessage('Files uploaded successfully');
    setRefreshKey(k => k + 1);
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const fireCount = fireFiles.loading ? '...' : fireFiles.total;
  const weatherCount = spotForecasts.loading ? '...' : spotForecasts.total;

  if (!CMS_URL) {
    return (
      <div className="wildfire-detail-content files-tab">
        <p className="files-warning">CMS URL not configured</p>
      </div>
    );
  }

  return (
    <div className="wildfire-detail-content files-tab">
      {statusMessage && <p className="status-message">{statusMessage}</p>}

      <div className="files-sub-tabs">
        <button
          type="button"
          className={`files-sub-tab ${activeTab === 'fire-files' ? 'files-sub-tab--active' : ''}`}
          onClick={() => setActiveTab('fire-files')}
        >
          Fire Files <span className="files-sub-tab__count">{fireCount}</span>
        </button>
        <button
          type="button"
          className={`files-sub-tab ${activeTab === 'spot-forecasts' ? 'files-sub-tab--active' : ''}`}
          onClick={() => setActiveTab('spot-forecasts')}
        >
          Spot Forecasts <span className="files-sub-tab__count">{weatherCount}</span>
        </button>
      </div>

      {activeTab === 'fire-files' && (
        <FileSection
          heading="Fire Files"
          cards={fireFiles.cards}
          total={fireFiles.total}
          hasMore={fireFiles.hasMore}
          loading={fireFiles.loading}
          error={fireFiles.error}
          viewAllUrl={viewAllUrl}
          emptyLabel={`No fire files found for ${fireNumber}.`}
          actions={
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { setStatusMessage(null); setIsUploaderOpen(true); }}
            >
              Upload Files
            </button>
          }
        />
      )}

      {activeTab === 'spot-forecasts' && (
        <FileSection
          heading="Spot Forecasts"
          cards={spotForecasts.cards}
          total={spotForecasts.total}
          hasMore={spotForecasts.hasMore}
          loading={spotForecasts.loading}
          error={spotForecasts.error}
          viewAllUrl={viewAllUrl}
          emptyLabel={`No spot forecasts found for ${fireNumber}.`}
        />
      )}

      <MultiFileUploader
        contentType="fire_file"
        context={{ wildfireId: wildfireUuid || undefined, fireNumber }}
        identifier={fireNumber}
        isOpen={isUploaderOpen}
        onClose={() => setIsUploaderOpen(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};
